/**
 * useCollabRoom — minimal WebSocket hook for realtime canvas collab.
 *
 * Connects to /api/ws/collab/<roomId>, broadcasts via `send()`, and calls
 * `onMessage(msg)` on every inbound payload (peer_id + ts attached by the
 * server). Auto-reconnects with exponential backoff.
 *
 * Usage:
 *   const { send, peerCount, status, clientId } = useCollabRoom(roomId, {
 *     onMessage: (m) => { if (m.kind === 'stroke') applyStroke(m); },
 *   });
 *
 * Pass null/empty roomId to disable. Switching rooms tears the previous
 * socket down cleanly.
 */
import { useEffect, useRef, useState, useCallback } from 'react';

function wsUrl(roomId) {
  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
  if (!base) return null;
  const wsProto = base.startsWith('https') ? 'wss' : 'ws';
  const host = base.replace(/^https?:\/\//, '');
  return `${wsProto}://${host}/api/ws/collab/${encodeURIComponent(roomId)}`;
}

export default function useCollabRoom(roomId, { onMessage } = {}) {
  const [status, setStatus] = useState('idle'); // idle | connecting | open | closed
  const [peerCount, setPeerCount] = useState(0);
  const [clientId, setClientId] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const attemptsRef = useRef(0);
  const onMsgRef = useRef(onMessage);

  useEffect(() => { onMsgRef.current = onMessage; }, [onMessage]);

  const connect = useCallback(() => {
    if (!roomId) { setStatus('idle'); return; }
    const url = wsUrl(roomId);
    if (!url) return;
    setStatus('connecting');
    let ws;
    try {
      ws = new WebSocket(url);
    } catch (_e) {
      // Some browsers throw on bad URL — bail.
      setStatus('closed');
      return;
    }
    wsRef.current = ws;
    ws.onopen = () => {
      attemptsRef.current = 0;
      setStatus('open');
    };
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.kind === 'welcome') {
          setClientId(msg.client_id);
          setPeerCount(msg.peer_count || 1);
        } else if (msg.kind === 'presence') {
          if (typeof msg.peer_count === 'number') setPeerCount(msg.peer_count);
        }
        if (onMsgRef.current) onMsgRef.current(msg);
      } catch (_e) { /* ignore */ }
    };
    ws.onclose = () => {
      setStatus('closed');
      // Exponential backoff with cap at 10s
      const delay = Math.min(10000, 500 * Math.pow(2, attemptsRef.current));
      attemptsRef.current += 1;
      reconnectTimer.current = setTimeout(connect, delay);
    };
    ws.onerror = () => { /* let onclose handle it */ };
  }, [roomId]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) ws.close();
      wsRef.current = null;
    };
  }, [connect]);

  const send = useCallback((payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify(payload));
      return true;
    } catch (_e) { return false; }
  }, []);

  return { send, status, peerCount, clientId };
}
